import React, { useContext, useState } from "react";
import { TranslatorContext } from "../../context/Translator";
import { ButtonComponent, AnchorComponent } from "../../components/elements";
import IconFieldComponent from "../../components/fields/IconFieldComponent";
import LogoComponent from "../../components/LogoComponent";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useValidation } from "../../context/ValidationContext";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { validateEmail } = useValidation();
  const { t } = useContext(TranslatorContext);

  const [step, setStep] = useState(1); // 1=email, 2=otp, 3=password

  const [values, setValues] = useState({
    email: "",
    otp: "",
    password: "",
  });

  const [errors, setErrors] = useState({});

  const validatePassword = (password) => password.length >= 8;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  /* ================= STEP 1: SEND OTP ================= */
  const sendOtp = async (e) => {
    e.preventDefault();

    if (!values.email || !validateEmail(values.email)) {
      setErrors({ email: "Valid email required" });
      return;
    }

    try {
      await axios.post(
        process.env.REACT_APP_API_URL + "/send-login-otp",
        { email: values.email }
      );
      setStep(2);
    } catch (err) {
      setErrors({ email: "Failed to send OTP" });
    }
  };

  /* ================= STEP 2: VERIFY OTP ================= */
  const verifyOtp = async (e) => {
    e.preventDefault();

    if (!values.otp) {
      setErrors({ otp: "OTP is required" });
      return;
    }

    try {
      await axios.post(
        process.env.REACT_APP_API_URL + "/verify-login-otp",
        {
          email: values.email,
          otp: values.otp,
        }
      );
      setStep(3);
    } catch (err) {
      setErrors({ otp: "Invalid OTP" });
    }
  };

  /* ================= STEP 3: LOGIN ================= */
  const handleLogin = async (e) => {
    e.preventDefault();

    if (!values.password || !validatePassword(values.password)) {
      setErrors({ password: "Password must be at least 8 characters" });
      return;
    }

    try {
      const res = await axios.post(
        process.env.REACT_APP_API_URL + "/login",
        {
          email: values.email,
          password: values.password,
        }
      );

      if (res.data.success) {
        localStorage.setItem("authToken", res.data.token);
        localStorage.setItem("user", JSON.stringify(res.data.user));
        login(res.data);
        window.location.href = "/";
      } else {
        setErrors({ password: "Invalid credentials" });
      }
    } catch (error) {
      setErrors({ password: "Invalid email or password" });
    }
  };

  return (
    <div className="mc-auth">
      <img
        src="images/pattern.webp"
        alt="pattern"
        className="mc-auth-pattern"
      />

      <div className="mc-auth-group">
        <LogoComponent
          src="/images/fsf.png"
          alt="logo"
          href="/"
          className="mc-auth-logo"
        />

        <h4 className="mc-auth-title">Login to Families Set Free</h4>

        <form className="mc-auth-form">
          {/* STEP 1: EMAIL */}
          {step === 1 && (
            <>
              <IconFieldComponent
                icon="email"
                type="email"
                name="email"
                value={values.email}
                onChange={handleChange}
                placeholder="Enter your email"
                classes="w-100 h-sm"
              />
              {errors.email && (
                <span className="text-danger">{errors.email}</span>
              )}

              <ButtonComponent
                className="mc-auth-btn h-sm"
                onClick={sendOtp}
              >
                Send OTP
              </ButtonComponent>
            </>
          )}

          {/* STEP 2: OTP */}
          {step === 2 && (
            <>
              <IconFieldComponent
                icon="key"
                type="text"
                name="otp"
                value={values.otp}
                onChange={handleChange}
                placeholder="Enter OTP"
                classes="w-100 h-sm"
              />
              {errors.otp && (
                <span className="text-danger">{errors.otp}</span>
              )}

              <ButtonComponent
                className="mc-auth-btn h-sm"
                onClick={verifyOtp}
              >
                Verify OTP
              </ButtonComponent>
            </>
          )}

          {/* STEP 3: PASSWORD */}
          {step === 3 && (
            <>
              <IconFieldComponent
                icon="lock"
                type="password"
                name="password"
                value={values.password}
                onChange={handleChange}
                placeholder="Enter your password"
                classes="w-100 h-sm"
                passwordVisible={true}
              />
              {errors.password && (
                <span className="text-danger">{errors.password}</span>
              )}

              <ButtonComponent
                className="mc-auth-btn h-sm"
                onClick={handleLogin}
              >
                {t("sign_in")}
              </ButtonComponent>

              <AnchorComponent
                className="mc-auth-forgot"
                to="/forgot-password"
              >
                {t("forgot_password")}
              </AnchorComponent>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
